import { createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { ArticleDetailScreen } from '@/common/ArticleDetailScreen';
import { NotFoundScreen } from '@/common/ErrorScreen';

/**
 * A student news single: title, date, author avatar and body, and nothing else,
 * as in legacy.
 *
 * Keyed on slug; the fullapp twin keys on numeric id, which is why this is a
 * separate operation.
 */
function StudentNewsPost(): ReactElement | null {
  const { t } = useTranslation();
  const { slug } = Route.useParams();
  const { data, isError, isPending } = api.getStudentNewsPost.useQuery({
    path: { slug },
  });

  // Legacy 404s server-side, so a refusal is the not-found screen. Any error
  // gets it, as on the sibling detail routes.
  if (isError) return <NotFoundScreen />;
  // `ArticleDetailScreen` renders `notFoundMessage` whenever `article` is
  // falsy, so passing `data` while it loads would flash "Page not found" on
  // every successful load.
  if (isPending || data === undefined) return null;

  return (
    <ArticleDetailScreen
      article={{
        title: data.title,
        content: data.content,
        // Already formatted server-side in the site's time zone, not an instant
        // to format here.
        date: data.published_on,
        imageUrl: data.image_url ?? undefined,
        authorName: data.author_name,
        authorAvatarUrl: data.author_avatar_url,
      }}
      backPath="/news/"
      notFoundMessage={t('wbs.page-not-found')}
    />
  );
}

export const Route = createFileRoute('/_app/news/$slug')({
  component: StudentNewsPost,
});
